import { client, handleRequest, handleRequestBody } from "./client.ts";
import type { Construction } from "./constructions.ts";
import type { StageStatus } from "./stages.ts";

export type ActualDatesSource = "derived" | "manual";

export interface ScheduleTreeItem {
	stage_id: string;
	parent_stage_id?: string;
	name: string;
	status: StageStatus;
	position: number;
	depth: number;
	own_planned_start_date: string | null;
	own_planned_end_date: string | null;
	planned_start_date: string | null;
	planned_end_date: string | null;
	own_actual_start_date: string | null;
	own_actual_end_date: string | null;
	actual_start_date: string | null;
	actual_end_date: string | null;
	actual_dates_source: ActualDatesSource;
}

export interface SchedulePlannedSpan {
	planned_start_date: string | null;
	planned_end_date: string | null;
}

export interface ListSchedulesResponse {
	count: number;
	items: ScheduleTreeItem[];
	span: SchedulePlannedSpan;
	construction: Construction;
}

export function listSchedules(
	constructionId: string,
): Promise<ListSchedulesResponse> {
	return handleRequestBody(() =>
		client.get<ListSchedulesResponse>(
			`/constructions/${constructionId}/schedules`,
		),
	);
}

export interface ScheduleWindowEntry {
	planned_start_date: string | null;
	planned_end_date: string | null;
	own_planned_start_date: string | null;
	own_planned_end_date: string | null;
}

export interface GetScheduleWindowResponse {
	schedules: Record<string, ScheduleWindowEntry>;
	construction: Construction;
}

export function getScheduleWindow(
	constructionId: string,
	from: string,
	to: string,
): Promise<GetScheduleWindowResponse> {
	return handleRequestBody(() =>
		client.get<GetScheduleWindowResponse>(
			`/constructions/${constructionId}/schedules/window`,
			{ params: { from, to } },
		),
	);
}

export interface StageSchedule {
	stage_id: string;
	planned_start_date: string;
	planned_end_date: string;
	actual_start_date: string | null;
	actual_end_date: string | null;
	actual_dates_source: ActualDatesSource;
	created_at: string;
	updated_at: string;
}

export interface GetStageScheduleResponse {
	schedule: StageSchedule;
	construction: Construction;
}

export function getStageSchedule(
	constructionId: string,
	stageId: string,
): Promise<GetStageScheduleResponse> {
	return handleRequestBody(() =>
		client.get<GetStageScheduleResponse>(
			`/constructions/${constructionId}/stages/${stageId}/schedule`,
		),
	);
}

export interface SetStageScheduleInput {
	plannedStartDate: string;
	plannedEndDate: string;
}

interface StageScheduleResponse {
	schedule: StageSchedule;
}

export async function setStageSchedule(
	constructionId: string,
	stageId: string,
	input: SetStageScheduleInput,
): Promise<StageSchedule> {
	const { schedule } = await handleRequestBody(() =>
		client.put<StageScheduleResponse>(
			`/constructions/${constructionId}/stages/${stageId}/schedule`,
			{
				planned_start_date: input.plannedStartDate,
				planned_end_date: input.plannedEndDate,
			},
		),
	);
	return schedule;
}

export async function deleteStageSchedule(
	constructionId: string,
	stageId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(
			`/constructions/${constructionId}/stages/${stageId}/schedule`,
		),
	);
}

export interface SetActualDatesInput {
	actualStartDate: string;
	actualEndDate?: string;
}

export async function setStageActualDates(
	constructionId: string,
	stageId: string,
	input: SetActualDatesInput,
): Promise<StageSchedule> {
	const { schedule } = await handleRequestBody(() =>
		client.put<StageScheduleResponse>(
			`/constructions/${constructionId}/stages/${stageId}/schedule/actual`,
			{
				actual_start_date: input.actualStartDate,
				actual_end_date: input.actualEndDate ?? null,
			},
		),
	);
	return schedule;
}

export async function clearStageActualDates(
	constructionId: string,
	stageId: string,
): Promise<StageSchedule> {
	const { schedule } = await handleRequestBody(() =>
		client.delete<StageScheduleResponse>(
			`/constructions/${constructionId}/stages/${stageId}/schedule/actual`,
		),
	);
	return schedule;
}
